import { useLevMultiplier, useModalProps, useOriginationFee, useProvider } from '@components/store/getters';
import { ethers } from 'ethers';
import { DescriptionText } from '../Modal/Modal.styles';
import {
  DescriptionSpan,
  DetailsGrid,
  SectionTitle,
  ArrowWrapper,
  Group,
  GroupHeading,
  Data,
  CoinWrapper,
} from './RouteAndFeesModal.styles';
import { ArrowRight, LogoTether, LogoUsdc, LogoDai, LogoOusd, LogoArch } from '@components/icons';
import { useMemo } from 'react';
import { roundNumber } from 'utils/roundNumber';

export default function RouteAndFeesModal() {
  const provider: ethers.providers.Provider = useProvider();
  const leverageMultiplier = useLevMultiplier();
  const originationFee = useOriginationFee();
  const modalProps = useModalProps();

  const changeToEtherum = async () => {
    //TODO: changed from send to emit, check
    provider.emit('wallet_switchEthereumChain', [{ chainId: '0x1' }]);
  };

  const leverageMultiplierFormatted = useMemo(() => {
    if (leverageMultiplier) return roundNumber(leverageMultiplier + 1, 2);
  }, [leverageMultiplier]);

  const originationFeePercentage = useMemo(() => {
    if (originationFee) return Number(originationFee) * 100;
  }, [originationFee]);

  const originationFeeInUsdFormatted = useMemo(() => {
    if (originationFee && modalProps?.estimatedPosition)
      return roundNumber(Number(originationFee) * modalProps?.estimatedPosition, 2);
  }, [originationFee, modalProps?.estimatedPosition]);

  const stableCoinSymbol = useMemo(() => {
    if (modalProps?.selectedStableCoin) {
      return modalProps.selectedStableCoin.value.toUpperCase();
    }
  }, [modalProps?.selectedStableCoin]);

  const stableCoinLogo = useMemo(() => {
    // console.log({ value: modalProps?.selectedStableCoin?.value });
    switch (modalProps?.selectedStableCoin?.value) {
      case 'usdc':
        return <LogoUsdc />;

      case 'dai':
        return <LogoDai />;

      default:
        return <LogoTether />;
    }
  }, [modalProps?.selectedStableCoin]);

  return (
    <>
      <DescriptionText>
        <DescriptionSpan>
          <em>
            The below data is an <b>estimation</b> only.
          </em>
        </DescriptionSpan>
      </DescriptionText>
      <DetailsGrid>
        <section>
          <SectionTitle>
            Deposit
            <ArrowWrapper>
              <ArrowRight />
            </ArrowWrapper>
          </SectionTitle>
          <Group>
            <GroupHeading>Your Deposit</GroupHeading>
            <Data>
              <div>
                <CoinWrapper>{stableCoinLogo}</CoinWrapper>
                {modalProps?.deposit} {stableCoinSymbol}
              </div>
            </Data>
          </Group>
        </section>
        <section>
          <SectionTitle>
            Transactions &amp; Fees
            <ArrowWrapper>
              <ArrowRight />
            </ArrowWrapper>
          </SectionTitle>
          <Group>
            <GroupHeading>Leverage Fee</GroupHeading>
            <Data>
              <div>
                <CoinWrapper>{stableCoinLogo}</CoinWrapper>
                {modalProps?.tokenFeeInUsd} {stableCoinSymbol} &rarr;&nbsp;&nbsp;
                <CoinWrapper>
                  <LogoArch />
                </CoinWrapper>
                {modalProps?.tokenFeeInArch} ARCH
              </div>
            </Data>
          </Group>
          <Group>
            <GroupHeading>Your Position Collateral</GroupHeading>
            <div>
              <em>via Curve</em>
            </div>
            <Data>
              <div>
                <CoinWrapper>{stableCoinLogo}</CoinWrapper>
                {stableCoinSymbol} &rarr;&nbsp;&nbsp;
                <CoinWrapper>
                  <LogoOusd />
                </CoinWrapper>
                {modalProps?.collateral} OUSD
              </div>
            </Data>
          </Group>
          <Group>
            <GroupHeading>Origination Fee</GroupHeading>
            <div>
              <em>from Collateral: {originationFeePercentage}% Leveraged Position</em>
            </div>
            <Data>
              <div>
                <CoinWrapper>
                  <LogoOusd />
                </CoinWrapper>
                {originationFeeInUsdFormatted} OUSD
              </div>
            </Data>
          </Group>
        </section>
        <section>
          <SectionTitle>x{leverageMultiplierFormatted} Position (Wrapped NFT)</SectionTitle>
          <Group>
            <GroupHeading>Your Leveraged Position (NFT)</GroupHeading>
            <Data>
              <div>Collateral: {modalProps?.collateral} OUSD</div>
              <div>Leverage: x{leverageMultiplierFormatted}</div>
              <div>Est. Borrowed: {roundNumber(modalProps?.estimatedPosition, 2)} OUSD</div>
            </Data>
          </Group>
        </section>
      </DetailsGrid>
    </>
  );
}
